import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { execute } from '@/graphql/execute';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageSkeleton from '@/layout/Skeleton';
import { deleteArchivedProjectMutation, restoreProjectMutation } from '@/mutations/project.mutation';
import { archivedProjectsQuery } from '@/queries/project.query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const formatBytes = (bytes: number | null | undefined) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let value = bytes;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i++;
  }
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[i]}`;
};

const formatDate = (d: string | Date | null | undefined, lang: string) =>
  d ? new Date(d).toLocaleDateString(lang) : '—';

const ARCHIVE_QUOTA_MULTIPLIER = 0.3;

const phantomQuotaBytes = (archiveBytes: number) => Math.floor(archiveBytes * ARCHIVE_QUOTA_MULTIPLIER);

const ArchivedProjectsPage: FunctionComponent = () => {
  const { t, i18n } = useTranslation('project');
  const queryClient = useQueryClient();
  usePageMeta({ title: [t('archived.title')] });

  const { data, isLoading } = useQuery({
    queryKey: ['archivedProjects'],
    queryFn: () => execute(archivedProjectsQuery),
    refetchInterval: 30_000,
  });

  const projects = data?.archivedProjects ?? [];

  const _handleRestore = async (id: string, organizationId: string) => {
    const toastId = toast.loading(t('archived.restoringInProgress'));
    try {
      await execute(restoreProjectMutation, { id });
      queryClient.invalidateQueries({ queryKey: ['archivedProjects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', organizationId] });
      toast.success(t('archived.restoreStarted'), { id: toastId });
    } catch (err: any) {
      toast.error(t('archived.restoreFailed', { error: err?.message ?? 'unknown' }), { id: toastId });
    }
  };

  const _handleDelete = async (id: string) => {
    const toastId = toast.loading(t('archived.deletingInProgress'));
    try {
      await execute(deleteArchivedProjectMutation, { id });
      queryClient.invalidateQueries({ queryKey: ['archivedProjects'] });
      toast.success(t('archived.deleteSuccess'), { id: toastId });
    } catch (err: any) {
      toast.error(t('archived.deleteFailed', { error: err?.message ?? 'unknown' }), { id: toastId });
    }
  };

  if (isLoading) {
    return (
      <PageSkeleton steps={[{ label: t('archived.title'), href: '/archived-projects' }]}>
        <div className='flex items-center justify-center h-64'>
          <Loader2 className='size-8 animate-spin' />
        </div>
      </PageSkeleton>
    );
  }

  return (
    <PageSkeleton steps={[{ label: t('archived.title'), href: '/archived-projects' }]}>
      <div className='p-6 max-w-5xl mx-auto'>
        <h1 className='text-2xl font-semibold mb-2'>{t('archived.title')}</h1>
        <p className='text-sm text-muted-foreground mb-6'>{t('archived.subtitle')}</p>

        {projects.length === 0 ? (
          <div className='border rounded-md p-8 text-center text-sm text-muted-foreground'>{t('archived.empty')}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('archived.colName')}</TableHead>
                <TableHead>{t('archived.colStatus')}</TableHead>
                <TableHead>{t('archived.colSize')}</TableHead>
                <TableHead>{t('archived.colArchivedAt')}</TableHead>
                <TableHead>{t('archived.colRetention')}</TableHead>
                <TableHead className='text-right'>{t('archived.colActions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {projects.map((p) => {
                const archiveBytes = p.archiveBytes ?? 0;
                const retentionActive = p.retentionUntil && new Date(p.retentionUntil) > new Date();
                const isTransitioning = ['RESTORE_REQUESTED', 'RESTORE_RETRIEVED', 'RESTORING'].includes(
                  p.archiveStatus,
                );
                return (
                  <TableRow key={p.id}>
                    <TableCell className='font-medium'>{p.name}</TableCell>
                    <TableCell>
                      <span
                        className={
                          p.archiveStatus === 'RESTORE_FAILED'
                            ? 'text-destructive'
                            : isTransitioning
                              ? 'text-amber-600 dark:text-amber-400'
                              : ''
                        }>
                        {t(`archived.status.${p.archiveStatus}`)}
                      </span>
                    </TableCell>
                    <TableCell>{formatBytes(archiveBytes)}</TableCell>
                    <TableCell>{formatDate(p.archivedAt, i18n.language)}</TableCell>
                    <TableCell>
                      {retentionActive ? formatDate(p.retentionUntil, i18n.language) : t('archived.retentionElapsed')}
                    </TableCell>
                    <TableCell className='text-right space-x-2'>
                      <RestoreDialog
                        project={p}
                        archiveBytes={archiveBytes}
                        disabled={isTransitioning}
                        onConfirm={() => _handleRestore(p.id, p.organizationId)}
                      />
                      <DeleteDialog
                        project={p}
                        archiveBytes={archiveBytes}
                        retentionActive={!!retentionActive}
                        onConfirm={() => _handleDelete(p.id)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </PageSkeleton>
  );
};

const RestoreDialog: FunctionComponent<{
  project: { id: string; name: string; retentionUntil?: string | Date | null };
  archiveBytes: number;
  disabled: boolean;
  onConfirm: () => void;
}> = ({ project, archiveBytes, disabled, onConfirm }) => {
  const { t, i18n } = useTranslation('project');
  const phantomBytes = phantomQuotaBytes(archiveBytes);
  const overlapTotalBytes = archiveBytes + phantomBytes;
  const inRetention = project.retentionUntil && new Date(project.retentionUntil) > new Date();

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size='sm' variant='outline' disabled={disabled}>
          {t('archived.restoreConfirm')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('archived.restoreModalTitle')}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className='space-y-2 text-sm'>
              <p>{t('archived.restoreSize', { size: formatBytes(archiveBytes) })}</p>
              <p>{t('archived.restoreTime')}</p>
              {inRetention && (
                <p className='text-amber-700 dark:text-amber-400'>
                  {t('archived.restoreOverlapStorage', {
                    date: formatDate(project.retentionUntil, i18n.language),
                    activeStorage: formatBytes(archiveBytes),
                    phantomStorage: formatBytes(phantomBytes),
                    totalStorage: formatBytes(overlapTotalBytes),
                    interpolation: { escapeValue: false },
                  })}
                </p>
              )}
              <p className='text-amber-700 dark:text-amber-400'>{t('archived.restoreLinksReset')}</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('edit.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{t('archived.restoreConfirm')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const DeleteDialog: FunctionComponent<{
  project: { id: string; name: string; retentionUntil?: string | Date | null };
  archiveBytes: number;
  retentionActive: boolean;
  onConfirm: () => void;
}> = ({ project, archiveBytes, retentionActive, onConfirm }) => {
  const { t, i18n } = useTranslation('project');
  const phantomBytes = phantomQuotaBytes(archiveBytes);

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size='sm' variant='destructive'>
          {t('archived.deleteConfirm')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('archived.deleteModalTitle')}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className='space-y-2 text-sm'>
              <p>{project.name}</p>
              {retentionActive && (
                <p className='text-amber-700 dark:text-amber-400'>
                  {t('archived.deletePhantomStorage', {
                    storage: formatBytes(phantomBytes),
                    date: formatDate(project.retentionUntil, i18n.language),
                    interpolation: { escapeValue: false },
                  })}
                </p>
              )}
              <p>{t('archived.deleteIrreversible')}</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('edit.cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>{t('archived.deleteConfirm')}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ArchivedProjectsPage;
